import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { groupInvoicesByMonth } from '../../utils/invoiceMonths';
import SowSelect from '../shared/SowSelect';
import PoSelect from '../shared/PoSelect';
import ContractSelect from '../shared/ContractSelect';
import InvoicePlowCheck from './InvoicePlowCheck';
import TimesheetBuilder from './TimesheetBuilder';

function fmtMoney(v) {
  if (v == null) return '—';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_CONFIG = {
  submitted:    { label: 'Submitted',    bg: 'bg-blue-100',   text: 'text-blue-700' },
  under_review: { label: 'Under Review', bg: 'bg-amber-100',  text: 'text-amber-700' },
  approved:     { label: 'Approved',     bg: 'bg-green-100',  text: 'text-green-700' },
  paid:         { label: 'Paid',         bg: 'bg-purple-100', text: 'text-purple-700' },
  rejected:     { label: 'Rejected',     bg: 'bg-red-100',    text: 'text-red-700' },
};

// The tabs along the top. Awaiting Sage is approved and not yet entered in
// Sage, which is the Finance team's "Enter in Sage" Actions as a list.
const FILTERS = [
  ['', 'All'], ['to_review', 'to review'], ['submitted', 'submitted'], ['under_review', 'under review'], ['approved', 'approved'],
  ['awaiting_sage', 'awaiting Sage'], ['paid', 'paid'], ['rejected', 'rejected'],
];

function StatusBadge({ status }) {
  const s = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return <span className={`text-[8px] px-2 py-0.5 rounded font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
}

// invoiceId opens one invoice, from a Finance Action's link or the approval email.
// 'to-review' is not an invoice: the review Action opens the list filtered to
// the invoices waiting for review.
const TO_REVIEW_LINK = 'to-review';

export default function CasualInvoicesPage({ invoiceId = null }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter]   = useState(invoiceId === TO_REVIEW_LINK ? 'to_review' : '');

  // Month groups, and the reader's own expand/collapse choices on top of each
  // month's default. A month absent from this map is showing its default.
  const [monthOverrides, setMonthOverrides] = useState({});
  const months = useMemo(
    () => groupInvoicesByMonth(invoices, { collapsePaid: statusFilter !== 'paid' }),
    [invoices, statusFilter],
  );
  // The month holding the open invoice always stays expanded, so marking that
  // invoice paid does not fold the row away from under the detail panel.
  const isCollapsed = (g) => {
    if (selected && g.invoices.some(i => i.id === selected)) return false;
    return g.key in monthOverrides ? monthOverrides[g.key] : g.defaultCollapsed;
  };
  const toggleMonth = (g) => setMonthOverrides(m => ({ ...m, [g.key]: !isCollapsed(g) }));

  // For allocation form
  const [sows, setSows]         = useState([]);
  const [pos, setPos]           = useState([]);
  const [allocForm, setAllocForm] = useState({ sow_id:'', po_id:'', allocated_amount:'', allocated_days:'', allocation_type:'labour', notes:'' });
  const [allocating, setAllocating] = useState(false);

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const loadInvoices = () => {
    setLoading(true);
    Promise.all([
      api.get('resource-invoices' + (statusFilter ? `?status=${statusFilter}` : '')),
      api.get('sows', { status: 'Active' }),
      api.get('purchase-orders'),
    ]).then(([inv, s, p]) => {
      setInvoices(inv || []);
      setSows(s || []);
      setPos(p || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // A new filter is a new list, so month choices made against the old one go.
  useEffect(() => { setMonthOverrides({}); loadInvoices(); }, [statusFilter]);

  // quiet keeps the panel on screen while it refreshes, so a message shown
  // beside the control just used is still there afterwards.
  const loadDetail = (id, { quiet = false } = {}) => {
    if (!quiet) setDetailLoading(true);
    api.get(`resource-invoices/${id}`).then(d => {
      setDetail(d);
      if (!quiet) {
        setReviewNotes(d.review_notes || '');
        setAllocForm(f => ({ ...f, allocated_amount: d.total_amount ? String(d.total_amount) : '' }));
      }
      setDetailLoading(false);
    }).catch(() => setDetailLoading(false));
  };

  const selectInvoice = (id) => {
    setSelected(id);
    setPeriodEdit(null);
    setContractEdit(null);
    setTotalEdit(null);
    setTotalSaved('');
    setStatusError('');
    setSageError('');
    loadDetail(id);
  };

  useEffect(() => {
    if (invoiceId === TO_REVIEW_LINK) { setStatusFilter('to_review'); return; }
    if (invoiceId && invoiceId !== selected) selectInvoice(invoiceId);
  }, [invoiceId]);

  // Reported under the buttons, where the click was.
  const [statusError, setStatusError] = useState('');
  const updateStatus = async (status) => {
    setUpdatingStatus(true); setStatusError('');
    try {
      await api.put(`resource-invoices/${selected}`, { status, review_notes: reviewNotes });
      loadInvoices();
      loadDetail(selected);
    } catch (err) {
      setStatusError(err?.message || 'Could not change the status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Entered in Sage closes the Finance team's Action for the invoice; Undo
  // takes it back and the Action opens again.
  const [sageBusy, setSageBusy] = useState(false);
  const [sageError, setSageError] = useState('');
  const setSageEntered = async (entered) => {
    setSageBusy(true); setSageError('');
    try {
      await api.post(`resource-invoices/${selected}/sage-entered`, { entered });
      loadInvoices();
      loadDetail(selected);
    } catch (err) {
      setSageError(err?.message || 'Could not record that. Please try again.');
    } finally {
      setSageBusy(false);
    }
  };

  // Period correction. Timesheets in Period is read from these dates, so the
  // detail reloads after a save to show the weeks the new period covers.
  const [periodEdit, setPeriodEdit] = useState(null); // { from, to, error, saving } while editing
  const savePeriod = async () => {
    setPeriodEdit(p => ({ ...p, saving: true, error: '' }));
    try {
      await api.put(`resource-invoices/${selected}`, { period_from: periodEdit.from, period_to: periodEdit.to });
      setPeriodEdit(null);
      loadInvoices();
      loadDetail(selected);
    } catch (err) {
      setPeriodEdit(p => ({ ...p, saving: false, error: err.message || 'Could not save the period' }));
    }
  };

  // The contract the invoice's days count against. An emailed invoice logged
  // before the Accounts Inbox asked for one has none, so its days count nowhere.
  const [contractEdit, setContractEdit] = useState(null); // { contract_id, days, error, saving } while editing
  const saveContract = async () => {
    setContractEdit(c => ({ ...c, saving: true, error: '' }));
    try {
      await api.put(`resource-invoices/${selected}`, { contract_id: contractEdit.contract_id || null, labour_days: contractEdit.days === '' ? null : parseFloat(contractEdit.days) });
      setContractEdit(null);
      loadInvoices();
      loadDetail(selected);
    } catch (err) {
      setContractEdit(c => ({ ...c, saving: false, error: err.message || 'Could not save the contract' }));
    }
  };

  // The total, set from the invoice's lines when the one submitted is wrong (a
  // contractor entering £0). The lines are before VAT, so the VAT is asked for.
  const [totalEdit, setTotalEdit] = useState(null); // { vat, error, saving } while editing
  const [totalSaved, setTotalSaved] = useState('');
  const saveTotal = async () => {
    setTotalEdit(t => ({ ...t, saving: true, error: '' }));
    try {
      const res = await api.put(`resource-invoices/${selected}/totals`, { vat_amount: totalEdit.vat === '' ? 0 : Number(totalEdit.vat) });
      setTotalEdit(null);
      setTotalSaved(`Total set to ${fmtMoney(res.total_incl_vat)} including VAT, from the lines.`);
      loadInvoices();
      loadDetail(selected, { quiet: true });
    } catch (err) {
      setTotalEdit(t => ({ ...t, saving: false, error: err.message || 'Could not set the total' }));
    }
  };

  const addAllocation = async () => {
    if (!allocForm.allocated_amount) return;
    setAllocating(true);
    try {
      await api.post(`resource-invoices/${selected}/allocations`, {
        sow_id: allocForm.sow_id || null,
        po_id: allocForm.po_id || null,
        allocated_amount: parseFloat(allocForm.allocated_amount),
        allocated_days: allocForm.allocated_days !== '' ? parseFloat(allocForm.allocated_days) : null,
        allocation_type: allocForm.allocation_type,
        notes: allocForm.notes || null,
      });
      setAllocForm({ sow_id:'', po_id:'', allocated_amount:'', allocated_days:'', allocation_type:'labour', notes:'' });
      loadDetail(selected);
    } finally {
      setAllocating(false);
    }
  };

  const removeAllocation = async (allocId) => {
    if (!window.confirm('Remove this allocation?')) return;
    await api.delete(`resource-invoice-allocations/${allocId}`);
    loadDetail(selected);
  };

  const DAYS_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];

  // Allocation totals
  const allocTotal = (detail?.allocations || []).reduce((s, a) => s + (parseFloat(a.allocated_amount) || 0), 0);
  const unallocated = (parseFloat(detail?.total_amount) || 0) - allocTotal;
  const allocSow = sows.find(s => String(s.id) === String(allocForm.sow_id));
  const allocSowDays = allocSow && allocSow.reaug_days != null ? Number(allocSow.reaug_days) : null;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">External Resource Invoices</h2>
          <p className="text-[9px] text-gray-500">Review, approve and allocate invoices against SOW / PO</p>
        </div>
        <div className="flex gap-1">
          {FILTERS.map(([s, label]) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2 py-0.5 text-[8px] font-semibold rounded border ${statusFilter === s ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4" style={{ alignItems: 'flex-start' }}>
        {/* Invoice list */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden" style={{ minWidth: 260, flexShrink: 0 }}>
          {loading && <div className="text-center py-8 text-gray-400 text-xs">Loading…</div>}
          {!loading && invoices.length === 0 && <div className="text-center py-8 text-gray-400 text-xs">No invoices found</div>}
          {months.map(g => {
            const collapsed = isCollapsed(g);
            return (
              <div key={g.key}>
                <button onClick={() => toggleMonth(g)}
                  className="w-full text-left px-3 py-1.5 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[8px] text-gray-400 w-2 flex-shrink-0">{collapsed ? '▸' : '▾'}</span>
                    <span className="text-[9px] font-bold text-navy truncate">{g.label}</span>
                    {g.allPaid && <span className="text-[7px] px-1 py-0.5 rounded font-semibold bg-purple-100 text-purple-700 flex-shrink-0">all paid</span>}
                  </span>
                  <span className="text-[8px] text-gray-500 flex-shrink-0">{g.count} · {fmtMoney(g.total)}</span>
                </button>
                {!collapsed && g.invoices.map(inv => {
                  const allocated = parseFloat(inv.allocated_total || 0);
                  const total = parseFloat(inv.total_amount || 0);
                  const isFullyAllocated = allocated >= total && total > 0;
                  return (
                    <div key={inv.id} onClick={() => selectInvoice(inv.id)}
                      className={`px-3 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${selected === inv.id ? 'bg-navy/5 border-l-2 border-l-navy' : ''}`}>
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold text-navy truncate">{inv.worker_name}</div>
                          <div className="text-[8px] text-gray-400">{inv.invoice_number || 'No number'}</div>
                          <div className="text-[8px] text-gray-400">{formatDate(inv.period_from)} – {formatDate(inv.period_to)}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[11px] font-bold text-navy">{fmtMoney(inv.total_amount)}</div>
                          <StatusBadge status={inv.status} />
                          {inv.sage_entered_at && inv.status === 'approved' && <div className="text-[7px] text-navy mt-0.5">✓ In Sage</div>}
                          {isFullyAllocated && <div className="text-[7px] text-green-600 mt-0.5">✓ Allocated</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="bg-white border border-gray-200 rounded flex-1 min-w-0">
            {detailLoading && <div className="text-center py-8 text-gray-400 text-xs">Loading…</div>}
            {!detailLoading && detail && (
              <div>
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-navy">{detail.worker_name}</div>
                      <div className="text-[9px] text-gray-400">{detail.invoice_number} · {formatDate(detail.invoice_date)}</div>
                      {!periodEdit ? (
                        <div className="text-[9px] text-gray-400">
                          Period: {formatDate(detail.period_from)} – {formatDate(detail.period_to)}
                          <button onClick={() => setPeriodEdit({ from: detail.period_from || '', to: detail.period_to || '', error: '', saving: false })}
                            className="ml-2 text-blue-600 hover:underline">Edit period</button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <div className="flex items-center gap-1.5 text-[9px]">
                            <input type="date" className="form-input text-[9px] py-0.5" value={periodEdit.from}
                              onChange={e => setPeriodEdit(p => ({ ...p, from: e.target.value, error: '' }))} />
                            <span className="text-gray-400">to</span>
                            <input type="date" className="form-input text-[9px] py-0.5" value={periodEdit.to}
                              onChange={e => setPeriodEdit(p => ({ ...p, to: e.target.value, error: '' }))} />
                            <button onClick={savePeriod} disabled={periodEdit.saving || !periodEdit.from || !periodEdit.to}
                              className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
                              {periodEdit.saving ? 'Saving…' : 'Save period'}</button>
                            <button onClick={() => setPeriodEdit(null)} className="text-gray-500 hover:underline">Keep current period</button>
                          </div>
                          {periodEdit.error
                            ? <div className="text-[8px] text-red-600 mt-0.5">{periodEdit.error}</div>
                            : <div className="text-[8px] text-gray-400 mt-0.5">Timesheets in Period will show the weeks starting in these dates.</div>}
                        </div>
                      )}
                      {!contractEdit ? (
                        <div className="text-[9px] text-gray-400">
                          {detail.contract_id
                            ? <>Contract: <span className="font-semibold text-navy">{detail.contract_reference || 'not found'}</span>{detail.labour_days != null ? ` · ${Number(detail.labour_days)}d billed` : ' · no days given'}</>
                            : <span className="text-amber-600">No contract, so these days count against nothing.</span>}
                          <button onClick={() => setContractEdit({ contract_id: detail.contract_id || '', days: detail.labour_days ?? '', error: '', saving: false })}
                            className="ml-2 text-blue-600 hover:underline">{detail.contract_id ? 'Change contract' : 'Put against a contract'}</button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <div className="flex flex-wrap items-start gap-1.5 text-[9px]">
                            <div className="flex-1" style={{ minWidth: 200 }}>
                              <ContractSelect workerId={detail.resource_worker_id} value={contractEdit.contract_id}
                                onChange={id => setContractEdit(c => ({ ...c, contract_id: id, error: '' }))} className="form-input text-[9px] py-0.5" />
                            </div>
                            <input type="number" min="0" step="0.5" placeholder="days" className="form-input text-[9px] py-0.5" style={{ width: 64 }}
                              value={contractEdit.days} onChange={e => setContractEdit(c => ({ ...c, days: e.target.value, error: '' }))} />
                            <button onClick={saveContract} disabled={contractEdit.saving || !contractEdit.contract_id}
                              className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
                              {contractEdit.saving ? 'Saving…' : 'Save contract'}</button>
                            <button onClick={() => setContractEdit(null)} className="text-gray-500 hover:underline whitespace-nowrap">Keep as it was</button>
                          </div>
                          {contractEdit.error && <div className="text-[8px] text-red-600 mt-0.5">{contractEdit.error}</div>}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-[18px] font-extrabold text-navy">{fmtMoney(detail.total_amount)}</div>
                      <StatusBadge status={detail.status} />
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-5">
                  {/* Invoice breakdown */}
                  <div>
                    <div className="text-[9px] font-bold text-gray-400 uppercase mb-2">Invoice Breakdown</div>
                    <div className="bg-gray-50 rounded p-3 space-y-1.5">
                      {Number(detail.labour_days) > 0 && detail.day_rate != null && detail.labour_total != null && (
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-600">Labour ({detail.labour_days}d @ {fmtMoney(detail.day_rate)}/day)</span>
                          <span className="font-semibold">{fmtMoney(detail.labour_total)}</span>
                        </div>
                      )}
                      {/* Day lines read from the document are listed day by day
                          under Days against the PLOW, so here they are one total. */}
                      {(() => {
                        const days = (detail.lines || []).filter(l => l.source && l.line_type === 'labour');
                        if (!days.length) return null;
                        return (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-600">Days ({days.length} line{days.length === 1 ? '' : 's'}, listed against the PLOW below)</span>
                            <span className="font-semibold">{fmtMoney(days.reduce((s, l) => s + (Number(l.amount) || 0), 0))}</span>
                          </div>
                        );
                      })()}
                      {(detail.lines || []).filter(l => !(l.source && l.line_type === 'labour')).map(l => (
                        <div key={l.id} className="flex justify-between text-[10px]">
                          <span className="text-gray-600">{l.description}</span>
                          <span className="font-semibold">{fmtMoney(l.amount)}</span>
                        </div>
                      ))}
                      {detail.expenses_total > 0 && !detail.lines?.length && (
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-600">Expenses</span>
                          <span className="font-semibold">{fmtMoney(detail.expenses_total)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-[11px] font-bold border-t border-gray-200 pt-1.5 mt-1.5">
                        <span>Total</span>
                        <span>{fmtMoney(detail.total_amount)}</span>
                      </div>
                      {Number(detail.vat_amount) > 0 && (
                        <>
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-600">VAT</span>
                            <span className="font-semibold">{fmtMoney(detail.vat_amount)}</span>
                          </div>
                          <div className="flex justify-between text-[10px] font-bold">
                            <span>Total with VAT</span>
                            <span>{fmtMoney(detail.total_incl_vat ?? Number(detail.total_amount || 0) + Number(detail.vat_amount))}</span>
                          </div>
                        </>
                      )}
                      {/* The checked lines and the submitted total disagree: offer to
                          set the total from the lines, beside the figure it changes. */}
                      {(() => {
                        const checked = (detail.lines || []).filter(l => l.source === 'document' || l.source === 'manual');
                        const net = Math.round(checked.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;
                        if (!checked.length || !(net > 0) || Math.abs(net - Number(detail.total_amount || 0)) < 0.005) {
                          return totalSaved ? <div className="text-[9px] text-green-700 pt-1">{totalSaved}</div> : null;
                        }
                        const blocked = detail.status === 'paid'
                          ? 'It is marked paid, so the total can no longer be changed here.'
                          : detail.sage_entered_at
                            ? 'It is entered in Sage: press Not in Sage after all first, so the figure in Sage gets corrected too.'
                            : null;
                        if (!totalEdit) {
                          return (
                            <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-[9px] text-amber-800">
                              The lines add up to {fmtMoney(net)}, but the invoice total is {fmtMoney(detail.total_amount)}.{' '}
                              {blocked
                                ? blocked
                                : <button onClick={() => { setTotalSaved(''); setTotalEdit({ vat: Number(detail.vat_amount) > 0 ? String(detail.vat_amount) : '', error: '', saving: false }); }}
                                    className="text-blue-600 hover:underline font-semibold">Correct the total</button>}
                            </div>
                          );
                        }
                        const vat = totalEdit.vat === '' ? 0 : Number(totalEdit.vat);
                        const vatOk = Number.isFinite(vat) && vat >= 0;
                        const gross = Math.round((net + (vatOk ? vat : 0)) * 100) / 100;
                        const pct = vatOk && vat > 0 ? `${(vat / net * 100).toFixed(1).replace(/\.0$/, '')}% of the lines` : 'No VAT, as for a contractor who is not VAT registered';
                        return (
                          <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-[9px] text-amber-800 space-y-1.5">
                            <div className="flex justify-between">
                              <span>Before VAT, from the {checked.length} line{checked.length === 1 ? '' : 's'}</span>
                              <span className="font-semibold">{fmtMoney(net)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span>
                                VAT
                                <button onClick={() => setTotalEdit(t => ({ ...t, vat: (Math.round(net * 20) / 100).toFixed(2), error: '' }))}
                                  className="ml-2 text-blue-600 hover:underline">Add VAT at 20%</button>
                              </span>
                              <span className="flex items-center gap-1">
                                £<input type="number" min="0" step="0.01" placeholder="0.00" value={totalEdit.vat}
                                  onChange={e => setTotalEdit(t => ({ ...t, vat: e.target.value, error: '' }))}
                                  className="form-input text-[9px] py-0.5 w-24 text-right" />
                              </span>
                            </div>
                            <div className={`text-right ${vatOk ? 'text-gray-500' : 'text-red-600'}`}>{vatOk ? pct : 'VAT has to be zero or more.'}</div>
                            <div className="flex justify-between font-bold border-t border-amber-200 pt-1">
                              <span>Total with VAT</span>
                              <span>{fmtMoney(gross)}</span>
                            </div>
                            <div className="flex items-center gap-2 pt-0.5">
                              <button onClick={saveTotal} disabled={totalEdit.saving || !vatOk}
                                className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
                                {totalEdit.saving ? 'Saving…' : `Set the total to ${fmtMoney(gross)}`}</button>
                              <button onClick={() => setTotalEdit(null)} className="text-gray-500 hover:underline">Keep {fmtMoney(detail.total_amount)}</button>
                            </div>
                            {totalEdit.error && <div className="text-red-600">{totalEdit.error}</div>}
                          </div>
                        );
                      })()}
                    </div>
                    {detail.notes && <div className="text-[9px] text-gray-500 mt-1">{detail.notes}</div>}
                  </div>

                  {/* The document, and each day it bills against the PLOW. */}
                  <InvoicePlowCheck detail={detail} onChanged={() => { loadInvoices(); loadDetail(selected, { quiet: true }); }} />

                  {/* Linked timesheets — days logged against THIS contract in the period.
                      The builder sits outside the two branches so it stays mounted,
                      and its message stays on screen, when a build fills the list. */}
                  {detail.period_from && detail.period_to && (
                    <div>
                      {detail.period_from && detail.period_to && !(detail.timesheets || []).length && (
                        <div>
                          <div className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Timesheets in Period</div>
                          <div className="text-[9px] text-amber-600">No timesheet days on this contract in weeks starting {formatDate(detail.period_from)} – {formatDate(detail.period_to)}.</div>
                        </div>
                      )}
                      {(detail.timesheets || []).length > 0 && (
                        <div>
                          <div className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Timesheets in Period</div>
                          <div className="text-[8px] text-gray-400 mb-2">Days on this contract's timesheets for the weeks starting in the invoice period, with where each week has got to.</div>
                          {detail.timesheets.map(ts => {
                            const d = Number(ts.days_worked) || 0;
                            const st = ts.approval_status || '';
                            // Days the contractor flagged as evenings, in week order.
                            const eve = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
                              .filter(day => (ts.evening_days || '').split(',').includes(day))
                              .map(day => day[0].toUpperCase() + day.slice(1));
                            return (
                              <div key={ts.id} className="flex items-center justify-between py-1 border-b border-gray-50 text-[9px]">
                                <span className="text-gray-600">Week of {formatDate(ts.week_start)}</span>
                                <div className="flex items-center gap-2">
                                  {eve.length > 0 && (
                                    <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700" title="Days worked as an evening">
                                      Evening: {eve.join(', ')}
                                    </span>
                                  )}
                                  <span className="text-navy font-semibold">{d.toFixed(2)}d</span>
                                  <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold ${st==='approved'?'bg-green-100 text-green-700':st==='pending'||st==='submitted'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-500'}`}>{st === 'pending' ? 'awaiting approval' : st}</span>
                                </div>
                              </div>
                            );
                          })}
                          {/* Timesheet days vs invoiced labour days — surfaces billing variance */}
                          {(() => {
                            const tsDays = detail.timesheets.reduce((s, t) => s + (Number(t.days_worked) || 0), 0);
                            // Days billed: as typed on the invoice, or else the day lines read off it.
                            const fromLines = detail.plow_match?.summary
                              ? detail.plow_match.summary.invoice.normal + detail.plow_match.summary.invoice.increased + detail.plow_match.summary.invoice.other
                              : null;
                            const invDays = Number(detail.labour_days) > 0 ? Number(detail.labour_days) : fromLines ?? (detail.labour_days != null ? Number(detail.labour_days) : null);
                            const drift = invDays != null && Math.abs(invDays - tsDays) > 0.5;
                            return (
                              <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-gray-200 text-[9px] font-semibold">
                                <span className="text-gray-500">Days logged (this contract): {tsDays.toFixed(2)}d</span>
                                {invDays != null && (
                                  <span className={drift ? 'text-amber-600' : 'text-green-600'}>
                                    {drift
                                      ? `⚠ Invoice bills ${invDays.toFixed(2)}d (${(invDays - tsDays > 0 ? '+' : '')}${(invDays - tsDays).toFixed(2)}d)`
                                      : `✓ matches invoiced ${invDays.toFixed(2)}d`}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      <TimesheetBuilder invoiceId={detail.id} workerName={detail.worker_name}
                        hasTimesheets={(detail.timesheets || []).length > 0}
                        onBuilt={() => loadDetail(selected, { quiet: true })} />
                    </div>
                  )}

                  {/* Status update */}
                  <div>
                    <div className="text-[9px] font-bold text-gray-400 uppercase mb-2">Status & Review Notes</div>
                    <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={2}
                      className="form-input mb-2" placeholder="Internal review notes…" />
                    <div className="flex gap-2 flex-wrap">
                      {['under_review','approved','paid','rejected'].map(s => (
                        <button key={s} onClick={() => updateStatus(s)} disabled={updatingStatus || detail.status === s}
                          className={`text-[9px] px-2.5 py-1 rounded border font-semibold ${detail.status===s?'opacity-40 cursor-not-allowed':''} ${s==='approved'||s==='paid'?'bg-green-50 text-green-700 border-green-300':s==='rejected'?'bg-red-50 text-red-600 border-red-300':'bg-gray-50 text-gray-600 border-gray-300'}`}>
                          {s.replace('_',' ')}
                        </button>
                      ))}
                    </div>
                    {statusError && <div className="text-[9px] text-red-600 mt-1">{statusError}</div>}

                    {/* Where it has got to, and who moved it. Approval is no longer
                        overwritten by a later step, but on an invoice paid before
                        that was fixed reviewed_* is the payer, the same as paid_*. */}
                    <div className="mt-2 space-y-0.5 text-[9px] text-gray-500">
                      {detail.reviewed_at && ['approved', 'paid'].includes(detail.status) && detail.reviewed_at !== detail.paid_at && (
                        <div>{detail.reviewed_by_name ? `Approved by ${detail.reviewed_by_name}` : 'Approved automatically on submission'} on {formatDate(detail.reviewed_at)}</div>
                      )}
                      {detail.sage_entered_at && <div>Entered in Sage by {detail.sage_entered_by_name || 'somebody no longer listed'} on {formatDate(detail.sage_entered_at)}</div>}
                      {detail.paid_at && detail.status === 'paid' && <div>Marked paid by {detail.paid_by_name || 'somebody no longer listed'} on {formatDate(detail.paid_at)}</div>}
                    </div>

                    {/* Before approval: say where it is waiting in the Accounts
                        Inbox, so nobody emails it there as well. */}
                    {detail.inbox?.email_id && ['submitted', 'under_review'].includes(detail.status) && (
                      <div className="mt-2 text-[9px] text-gray-500">
                        In the <a href={`#email-inbox/${detail.inbox.email_id}`} className="text-blue-600 hover:underline">Accounts Inbox</a>, waiting for approval. Approving it logs it there for Sage.
                      </div>
                    )}

                    {detail.status === 'approved' && (
                      <div className="mt-2 p-2 rounded border border-gray-200 bg-gray-50">
                        {!detail.sage_entered_at ? (
                          detail.inbox?.supplier_invoice_id ? (
                            // Logged in the Accounts Inbox at approval: posting it
                            // there is the way to Sage, and marks it entered here.
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <a href={`#email-inbox/${detail.inbox.email_id}`}
                                  className="text-[9px] px-2.5 py-1 rounded bg-navy text-white font-semibold hover:bg-navy/80">
                                  Post to Sage in the Accounts Inbox →</a>
                                <span className="text-[9px] text-gray-500">
                                  It is logged there for Sage. Posting it marks it entered in Sage here and closes the Finance team's Action.
                                </span>
                              </div>
                              <div className="text-[9px] text-gray-500">
                                Keyed into Sage by hand instead?{' '}
                                <button onClick={() => setSageEntered(true)} disabled={sageBusy}
                                  className="text-blue-600 hover:underline disabled:opacity-40">
                                  {sageBusy ? 'Saving…' : 'Mark it entered in Sage'}</button>
                                {' '}and it comes off the inbox's Not in Sage list.
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button onClick={() => setSageEntered(true)} disabled={sageBusy}
                                className="text-[9px] px-2.5 py-1 rounded bg-navy text-white font-semibold disabled:opacity-40">
                                {sageBusy ? 'Saving…' : '✓ Entered in Sage'}</button>
                              <span className="text-[9px] text-gray-500">Press once it is in Sage for payment. This closes the Finance team's Action for it.</span>
                            </div>
                          )
                        ) : detail.inbox?.sage_entered_by_inbox ? (
                          <div className="flex items-center gap-2 flex-wrap text-[9px]">
                            <span className="text-navy font-semibold">
                              ✓ Posted to Sage from the Accounts Inbox{detail.inbox.sage_invoice_number ? ` as ${detail.inbox.sage_invoice_number}` : ''}, awaiting payment.
                            </span>
                            {detail.inbox.email_id && (
                              <a href={`#email-inbox/${detail.inbox.email_id}`} className="text-blue-600 hover:underline">Open it there</a>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap text-[9px]">
                            <span className="text-navy font-semibold">✓ In Sage, awaiting payment.</span>
                            <button onClick={() => setSageEntered(false)} disabled={sageBusy}
                              className="text-blue-600 hover:underline disabled:opacity-40">Not in Sage after all</button>
                          </div>
                        )}
                        {sageError && <div className="text-[9px] text-red-600 mt-1">{sageError}</div>}
                      </div>
                    )}
                  </div>

                  {/* Allocations */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[9px] font-bold text-gray-400 uppercase">SOW / PO Allocation</div>
                      <div className="text-[9px] font-semibold">
                        Allocated: <span className="text-green-700">{fmtMoney(allocTotal)}</span>
                        {unallocated > 0.01 && <span className="text-amber-600 ml-2">Unallocated: {fmtMoney(unallocated)}</span>}
                        {unallocated <= 0.01 && allocTotal > 0 && (parseFloat(detail?.total_amount) || 0) > 0 && <span className="text-green-600 ml-2">✓ Fully allocated</span>}
                      </div>
                    </div>

                    {/* Existing allocations */}
                    {(detail.allocations || []).map(a => (
                      <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 text-[9px]">
                        <div>
                          <span className="font-semibold text-navy">{fmtMoney(a.allocated_amount)}</span>
                          {a.allocated_days != null && <span className="text-sky-600 ml-2">{Number(a.allocated_days).toFixed(1)}d</span>}
                          <span className="text-gray-400 ml-2">— {a.allocation_type}</span>
                          {a.sow_reference && <span className="text-gray-500 ml-2">{a.sow_reference}</span>}
                          {a.po_number && <span className="text-gray-500 ml-2">{a.po_number}</span>}
                          {a.notes && <span className="text-gray-400 ml-2 italic">{a.notes}</span>}
                        </div>
                        <button onClick={() => removeAllocation(a.id)} className="text-red-400 hover:text-red-600 text-[8px]">✕</button>
                      </div>
                    ))}

                    {/* Add allocation form */}
                    <div className="bg-gray-50 rounded p-3 mt-2 space-y-2">
                      <div className="text-[8px] font-semibold text-gray-500 uppercase">Add Allocation</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[8px] text-gray-500 mb-0.5">Type</label>
                          <select className="form-input text-[9px] py-1" value={allocForm.allocation_type} onChange={e => setAllocForm(f => ({...f,allocation_type:e.target.value}))}>
                            <option value="labour">Labour</option>
                            <option value="expenses">Expenses</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-500 mb-0.5">Amount (£)</label>
                          <input type="number" min="0" step="0.01" className="form-input text-[9px] py-1" value={allocForm.allocated_amount} onChange={e => setAllocForm(f => ({...f,allocated_amount:e.target.value}))} />
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-500 mb-0.5">Days</label>
                          <input type="number" min="0" step="0.5" className="form-input text-[9px] py-1" placeholder="optional" value={allocForm.allocated_days} onChange={e => setAllocForm(f => ({...f,allocated_days:e.target.value}))} />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[8px] text-gray-500 mb-0.5">SOW</label>
                          <SowSelect sows={sows} value={allocForm.sow_id} onChange={id => setAllocForm(f => ({...f,sow_id:id}))} placeholder="— none —" />
                          {allocSowDays != null && (
                            <div className="text-[8px] text-sky-600 mt-0.5">Provisioned on this SOW: {allocSowDays.toFixed(1)} days{allocSow.reaug_day_rate ? ` @ £${allocSow.reaug_day_rate}/day` : ''}</div>
                          )}
                        </div>
                        <div>
                          <label className="block text-[8px] text-gray-500 mb-0.5">PO</label>
                          <PoSelect pos={pos} value={allocForm.po_id} onChange={id => setAllocForm(f => ({...f, po_id: id}))} placeholder="— none —" inputClassName="form-input text-[9px] py-1 w-full" />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[8px] text-gray-500 mb-0.5">Notes</label>
                          <input className="form-input text-[9px] py-1" value={allocForm.notes} onChange={e => setAllocForm(f => ({...f,notes:e.target.value}))} placeholder="Optional note" />
                        </div>
                      </div>
                      <button onClick={addAllocation} disabled={allocating || !allocForm.allocated_amount}
                        className="w-full bg-navy text-white text-[10px] font-bold py-2 rounded hover:bg-navy/90 disabled:opacity-40 transition-colors">
                        {allocating ? 'Saving…' : 'Add Allocation'}</button>
                    </div>
                  </div>

                  {/* Traceability summary */}
                  <div className="bg-navy/5 rounded p-3 text-[9px]">
                    <div className="font-bold text-navy mb-2">Traceability</div>
                    <div className="space-y-1 text-gray-600">
                      <div>Worker: <span className="font-semibold text-navy">{detail.worker_name}</span> (@{detail.username})</div>
                      <div>Invoice: <span className="font-semibold">{detail.invoice_number}</span> — {fmtMoney(detail.total_amount)}</div>
                      {(detail.allocations||[]).map(a => (
                        <div key={a.id} className="ml-3">
                          ↳ {a.allocation_type}: {fmtMoney(a.allocated_amount)}
                          {a.sow_reference && ` → ${a.sow_reference}`}
                          {a.po_number && ` → PO ${a.po_number}`}
                        </div>
                      ))}
                      {detail.timesheets?.length > 0 && <div className="mt-1">Timesheets: {detail.timesheets.length} week(s), {detail.timesheets.reduce((s,t) => s + (Number(t.days_worked)||0), 0).toFixed(2)}d total (this contract)</div>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!selected && !loading && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-xs py-20">
            Select an invoice to review and allocate
          </div>
        )}
      </div>
    </div>
  );
}
